import GameState from '~/modules/game-engine/game-state/game-state';
import InputManager from '~/modules/game-engine/input/input-manager';
import events from '~/modules/game-events/game-events';
import { LOCAL_PLAYER_NUMBERS, PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import { preferLanRelayWhenHosted } from '~/modules/remote-mic/network/prefer-lan-relay';
import RemoteMicServer from '~/modules/remote-mic/network/server';
import RemoteMicManager from '~/modules/remote-mic/remote-mic-manager';
import { getAllStats } from '~/modules/songs/stats/common';
import { fetchStatsForNames, gradeOf } from '~/modules/user-profiles/profile-stats';
import UserProfiles, { ProfileWriteError } from '~/modules/user-profiles/profiles';
import isPreRendering from '~/modules/utils/is-pre-rendering';
import { RemoteMicrophoneInputSource } from '~/routes/select-input/input-sources/remote';
import { rememberSlot } from '~/routes/select-input/remote-mic-slots';

events.playerRemoved.subscribe((player) => {
  if (player.input.source === RemoteMicrophoneInputSource.inputName) {
    const remoteMic = RemoteMicManager.getRemoteMicById(player.input.deviceId!);
    if (remoteMic) {
      remoteMic.setPlayerNumber(null);
    }
  }
});

events.playerInputChanged.subscribe((playerNumber, oldInput, newInput) => {
  if (oldInput?.source === RemoteMicrophoneInputSource.inputName) {
    const playerNumber = PlayersManager.getPlayers().find(
      (player) => player.input.source === 'Remote Microphone' && player.input.deviceId === oldInput.deviceId,
    )?.number;
    const unselectedRemoteMic = RemoteMicManager.getRemoteMicById(oldInput.deviceId!);

    unselectedRemoteMic?.setPlayerNumber(playerNumber !== undefined ? playerNumber : null);
  }
  if (newInput?.source === 'Remote Microphone') {
    const selectedRemoteMic = RemoteMicManager.getRemoteMicById(newInput.deviceId!);

    selectedRemoteMic?.setPlayerNumber(playerNumber);
    // Recorded here rather than where the slot is picked, so a phone moved by hand - from the
    // host, or from its own colour picker - comes back to the slot the room last saw it in and
    // not the one it was first given.
    rememberSlot(newInput.deviceId!, playerNumber);
  }
});
events.remoteMicConnected.subscribe(({ id }) => {
  const playerNumberIndex = PlayersManager.getPlayers().find(
    (player) => player.input.source === 'Remote Microphone' && player.input.deviceId === id,
  )?.number;

  if (playerNumberIndex !== undefined) {
    const remoteMic = RemoteMicManager.getRemoteMicById(id);

    remoteMic?.setPlayerNumber(playerNumberIndex);

    if (InputManager.monitoringStarted()) {
      remoteMic?.getInput()?.startMonitoring();
    }
  }
});

// A `startMonitor` sent while the game's socket was reconnecting never reached the phone, which
// then sang into nothing for the rest of the song. Asking again is harmless to a phone that heard.
events.micServerStarted.subscribe(() => {
  if (!InputManager.monitoringStarted()) return;

  PlayersManager.getPlayers().forEach((player) => {
    if (player.input.source !== RemoteMicrophoneInputSource.inputName) return;
    RemoteMicManager.getRemoteMicById(player.input.deviceId!)?.getInput().startMonitoring();
  });
});

/**
 * Why a phone may not take this seat, or null when it may. A slot a dropped phone still holds is
 * kept for that phone on purpose, so it counts as taken like any other singer's.
 */
export const seatRequestRefusal = (phoneId: string, playerNumber: PlayerNumber | null): string | null => {
  // Leaving counts too: a singer dropped mid-song vanishes from the scores and the lanes.
  if (GameState.isPlaying()) return 'A song is playing';
  if (playerNumber === null) return null;
  if (!LOCAL_PLAYER_NUMBERS.includes(playerNumber)) return 'No such seat';

  const input = PlayersManager.getPlayer(playerNumber)?.input;
  if (!input || input.source === 'Dummy') return null;
  if (input.source === RemoteMicrophoneInputSource.inputName && input.deviceId === phoneId) return null;
  return 'Seat taken';
};

// Thrown rather than ignored: the event is dispatched from the phone's RPC, which then rejects.
events.playerChangeRequested.subscribe((phoneId, newPlayerNumber) => {
  const refusal = seatRequestRefusal(phoneId, newPlayerNumber);
  if (refusal) throw new Error(refusal);

  const currentPlayer = PlayersManager.getPlayers().find((player) => player.input.deviceId === phoneId);

  if (currentPlayer) {
    PlayersManager.removePlayer(currentPlayer.number);
  }
  if (newPlayerNumber !== null) {
    const player = PlayersManager.getPlayer(newPlayerNumber);

    if (player) {
      player.changeInput('Remote Microphone', 0, phoneId);
    } else {
      PlayersManager.addPlayer(newPlayerNumber)?.changeInput('Remote Microphone', 0, phoneId);
    }
  }
});

events.remoteMicConnected.subscribe(async ({ name, id, silent }) => {
  if (!isPreRendering) {
    if (!silent) {
      (await import('react-toastify')).toast.success(
        <span>
          Remote microphone <b className="ph-no-capture">{name}</b> connected!
        </span>,
        {
          toastId: `connection-status-${id}`,
          updateId: `connection-status-${id}`,
        },
      );
    }
  }
});
events.remoteMicDisconnected.subscribe(async ({ name, id }, silent) => {
  if (!isPreRendering) {
    if (!silent) {
      (await import('react-toastify')).toast.warning(
        <span>
          Remote microphone <b className="ph-no-capture">{name}</b> disconnected!
        </span>,
        {
          toastId: `connection-status-${id}`,
          updateId: `connection-status-${id}`,
        },
      );
    }
  }
});

const sendPlayerStates = () => {
  const remoteMics = RemoteMicManager.getRemoteMics().map((mic) => ({
    id: mic.id,
    name: mic.name,
    number: PlayersManager.getPlayers().find((player) => player.input.deviceId === mic.id)?.number ?? null,
  }));

  RemoteMicServer.publish('remote-mics', remoteMics);
};

events.playerRemoved.subscribe(sendPlayerStates);
events.inputListChanged.subscribe(sendPlayerStates);
events.playerInputChanged.subscribe(sendPlayerStates);
events.remoteMicConnected.subscribe(sendPlayerStates);
events.remoteMicDisconnected.subscribe(sendPlayerStates);
events.remoteMicRenamed.subscribe(sendPlayerStates);
// Publish the current state immediately so new channel subscribers receive
// the latest list without waiting for the next player/input event.
sendPlayerStates();

// The play history the phone's Played filter needs. Plays are filed under the song's own id
// (`getSongKey` forwards to `getSongId`), which is the id the phone knows it by too, so the keys
// are the answer and nothing has to be mapped. Republished when a play is stored, so finishing a
// song moves it out of "Never played" on every phone in the room without a reload.
const sendPlayedSongs = async () => {
  const stats = await getAllStats();
  const played = Object.entries(stats)
    .filter(([, songStats]) => (songStats?.plays ?? 0) > 0)
    .map(([id]) => id);

  RemoteMicServer.publish('played-songs', played);
};

events.songStatStored.subscribe(() => void sendPlayedSongs());
// Published once up front as well: the registry caches the last value and hands it to a phone the
// moment it subscribes, so a room that has not finished a song since loading still answers.
if (!isPreRendering) void sendPlayedSongs();

// A phone names its singer itself - "New singer" on the join flow, or a rename from its top bar -
// and that name is who the round's scores are filed under. Without a profile behind it the singer
// sang, scored and was never on the Profiles screen. Registered ahead of `publishSingers` below, so
// the list the phones are sent already has them in it.
const ensurePhoneSingerProfile = ({ name }: { name: string }) => {
  if (isPreRendering) return;

  try {
    UserProfiles.ensure(name);
  } catch (error) {
    // A full localStorage only costs the profile; the phone still sings and its scores still land.
    if (!(error instanceof ProfileWriteError)) throw error;
    console.warn('Could not create a profile for a phone singer', error);
  }
};

events.remoteMicConnected.subscribe(ensurePhoneSingerProfile);
events.remoteMicRenamed.subscribe(ensurePhoneSingerProfile);

// The singers the room knows, for the phone's join flow. Everyone with a profile, plus anybody a
// score was ever recorded for - a play is filed under a plain name (`SongStats.scores[].name`), so
// somebody who sang once without making a profile is still a singer this room recognises and is
// exactly the person most likely to mistype their way into a second history.
//
// `heldByMicId` is read from the connected mics rather than stored, so it is right after a host
// reload without anything to reconcile.
const sendSingers = async () => {
  const stats = await getAllStats();

  const names = new Map<string, string>();
  const remember = (name: string) => {
    const trimmed = name.trim();
    if (trimmed) names.set(trimmed.toLowerCase(), trimmed);
  };

  UserProfiles.getAll().forEach((profile) => remember(profile.name));
  Object.values(stats).forEach((songStats) =>
    songStats?.scores?.forEach((round) => round.scores.forEach((score) => remember(score.name))),
  );

  const uniqueNames = [...names.values()];
  const statsByName = await fetchStatsForNames(uniqueNames);

  const singers = uniqueNames
    .map((name) => {
      const profileStats = statsByName[name];
      const plays = profileStats?.plays.length ?? 0;

      return {
        name,
        plays,
        bestGrade: profileStats?.bestPlay ? gradeOf(profileStats.bestPlay) : null,
        heldByMicId: RemoteMicManager.getMicHoldingName(name)?.id ?? null,
      };
    })
    // Most-sung first: the person picking themselves off this list is usually a regular, and a
    // room's regulars are the ones with the plays.
    .sort((a, b) => b.plays - a.plays || a.name.localeCompare(b.name));

  RemoteMicServer.publish('singers', singers);
};

// Swallowed rather than left to reject: the singer list is an offer on one step of the join flow,
// and a stats read that fails must not take the host's event handling down with it.
const publishSingers = () => void sendSingers().catch((error) => console.warn('Could not publish singers', error));

events.songStatStored.subscribe(publishSingers);
events.remoteMicConnected.subscribe(publishSingers);
events.remoteMicDisconnected.subscribe(publishSingers);
events.remoteMicRenamed.subscribe(publishSingers);
if (!isPreRendering) publishSingers();

// Which song is on, so the phone can show the singing screen and lock the tabs behind it. Published
// by the singing screen itself, for its own lifetime - see `singing.tsx`. Said once here as well, so
// a host that reloaded mid-song tells every phone that subscribes afterwards that nothing is
// playing, rather than leaving them on whatever they were last told.
if (!isPreRendering) RemoteMicServer.publish('current-song', undefined);

preferLanRelayWhenHosted();
